"use client";

import { useEffect, useRef, useState } from "react";
import { analyticsEnabled } from "@/lib/analytics-preferences";

type CalendlyWidget = {
  initInlineWidget: (options: { url: string; parentElement: HTMLElement; prefill?: { name: string; email: string } }) => void;
};

declare global {
  interface Window {
    Calendly?: CalendlyWidget;
  }
}

const SCRIPT_URL = "https://assets.calendly.com/assets/external/widget.js";

export function CalendlyBooking({ url, source, reference, prefill, compact = false }: { url: string; source: string; reference: string; prefill?: { name: string; email: string }; compact?: boolean }) {
  const [booked, setBooked] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [widgetHeight, setWidgetHeight] = useState<number | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const eventUrl = new URL(url);
  eventUrl.searchParams.set("utm_source", "dbm-website");
  eventUrl.searchParams.set("utm_medium", "thank-you");
  eventUrl.searchParams.set("utm_campaign", source);
  eventUrl.searchParams.set("utm_content", reference);
  if (compact) eventUrl.searchParams.set("hide_event_type_details", "1");
  const bookingUrl = eventUrl.toString();
  const directUrl = new URL(bookingUrl);
  if (prefill) {
    directUrl.searchParams.set("name", prefill.name);
    directUrl.searchParams.set("email", prefill.email);
  }

  useEffect(() => {
    if (!container.current) return;
    const element = container.current;
    const init = () => {
      if (!window.Calendly || !element || element.childElementCount) return;
      window.Calendly.initInlineWidget({ url: bookingUrl, parentElement: element, prefill });
    };
    if (window.Calendly) {
      init();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = init;
    script.onerror = () => setLoadError(true);
    document.body.appendChild(script);
    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, [bookingUrl, prefill]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== "https://calendly.com" || event.source !== container.current?.querySelector("iframe")?.contentWindow) return;
      if (event.data?.event === "calendly.page_height") {
        const height = Number.parseInt(event.data.payload?.height, 10);
        if (Number.isFinite(height) && height >= 500 && height <= 2000) setWidgetHeight(height + 24);
        return;
      }
      if (event.data?.event !== "calendly.event_scheduled") return;
      setBooked(true);
      if (!analyticsEnabled()) return;
      const payload = { event: "meeting_booked", lead_source: source, lead_reference: reference };
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push(payload);
      window.gtag?.("event", "meeting_booked", { lead_source: source, lead_reference: reference });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [reference, source]);

  return (
    <section className={compact ? "booking-section consultation-calendar" : "booking-section"} aria-labelledby="booking-title">
      <div className="container-dbm booking-container text-center">
        <h2 className="h-section" id="booking-title">{compact ? "Choose your consultation time" : "Choose a time to talk"}</h2>
        <p className="lead booking-description">Book a conversation with our team now, or we can follow up to coordinate.</p>
        <div id="calendly-scheduler" className="booking-panel text-left">
          {booked ? <p className="p-4 text-center font-bold text-dbm-blue-text" role="status">Your meeting is booked. Check your inbox for the calendar invitation.</p> : null}
          <div ref={container} className="booking-widget" style={widgetHeight ? { height: widgetHeight } : undefined} />
          {loadError ? <p className="p-4 text-center text-dbm-muted">The calendar could not load. Use the booking link below.</p> : null}
          <p className="py-3 text-center text-sm text-dbm-muted">
            <a className="font-bold text-dbm-blue-text" href={directUrl.toString()} target="_blank" rel="noopener noreferrer">Open booking page in a new tab</a>
          </p>
        </div>
      </div>
    </section>
  );
}
